import React, { FC, useEffect, useState } from 'react';
import cn from 'classnames';

import { Paginator, Skeleton, TooltipWrapper } from '@ui';
import useTranslation from '@app/hooks/use-translation';
import { useGetRecordsQuery } from '@app/store/api/records.api';
import { PaginationResponse } from '@app/components/ui/paginator/paginator';
import { FilterTableData, TableTitle } from '@app/components/table/table.type';
import { navigationRoutes } from '@app/utils/navigation-routes';
import TimeDuration from '@app/pages/analytics/project-records/blocks/time-duration';
import { DownloadCloudIcon, Oscilogram } from '@icons';

import { GetFilterParamsNew } from '@app/components/ui/easy-filter/types';
import { tableUtilIconsClassName } from '@app/pages/records/blocks/records-table/records-table.styles';
import { getRecordAudio } from '@app/api/record.api';
import fileDownload from 'js-file-download';
import { useGetAliasesListQuery } from '@app/store/api/alias-settings-page.api';
import { useAppSelector } from '@app/store/store';
import { useUpdateUserSettingsMutation } from '@app/store/api/user-settings.api';
import { ChartsLayoutType } from '@app/interfaces/dashboards.type';
import { useOutletContext, useParams } from 'react-router-dom';
import Table from '@app/components/table';
import { useGetChatsQuery } from '@app/store/api/chat-api/all-chats.api';

import { AnalyticPageOutlet } from '../../single-analytic.page';

type RecordsTableBlockPropsType = {
  recordsParams: GetFilterParamsNew;
  getRecordsParams: React.Dispatch<React.SetStateAction<GetFilterParamsNew>>;
};

export const PresetPageTableBlock: FC<RecordsTableBlockPropsType> = (props) => {
  const { recordsParams, getRecordsParams } = props;
  const { id } = useParams();
  const { currentTemplate } = useOutletContext<AnalyticPageOutlet>();

  const { userSettings } = useAppSelector((state) => state.userSettings);
  const [updateUserSettings, { isLoading: userLoading }] = useUpdateUserSettingsMutation();
  const { t } = useTranslation('pages.presetAp');
  const { t: tf } = useTranslation('pages.allRecords');
  const pageOptionsData = [
    { title: '10 ' + t('table.calls'), value: 10 },
    { title: '15 ' + t('table.calls'), value: 15 },
    { title: '25 ' + t('table.calls'), value: 25 },
    { title: '50 ' + t('table.calls'), value: 50 },
  ];

  // const [deleteRecordIds, changeDeleteRecordsIds] = useState<Array<string>>([]);
  const { data: recordsListChat, isLoading: isLoadingChat } = useGetChatsQuery(recordsParams, {
    skip: currentTemplate === 'voice',
  });

  const { data: recordsListSpeech, isLoading: isLoadingSpeech } = useGetRecordsQuery(
    recordsParams,
    { skip: currentTemplate === 'chat' },
  );
  const isLoading = isLoadingChat || isLoadingSpeech;
  const recordsList = { chat: recordsListChat, voice: recordsListSpeech };
  const recordDataByTemplate = {
    chat: recordsListChat?.chats,
    voice: recordsListSpeech?.records,
  };
  const idByType = {
    chat: 'chat_id',
    voice: 'record_id',
  };
  // const [deleteRecordRequest, { isLoading: isDeleteLoading }] = useDeleteRecordsMutation();
  const { data } = useGetAliasesListQuery();
  const checkingAliasForSorting = (alias_id: string) => {
    const alias = data?.aliases.find((alias) => alias.alias_id === alias_id);
    if (alias) {
      return alias.type_meta !== 'channel';
    }
    return false;
  };
  const [userSettingsLoading, setUserSettingsLoading] = useState(false);
  useEffect(() => {
    if (userLoading) {
      setUserSettingsLoading(true);
    }
    if (!userLoading) {
      setTimeout(() => setUserSettingsLoading(false), 300);
    }
  }, [userLoading]);
  const tableTitles: TableTitle[] = recordsList[currentTemplate || 'voice']?.headers.length
    ? [
        {
          index: 'duration_records_items',
          title: t('table.first_column_title'),
          size: 160,
        },
        ...(recordsList?.[currentTemplate || 'voice']?.headers || []).map((column) => ({
          index: column.alias_id,
          title: column.name,
          filter: checkingAliasForSorting(column.alias_id),
        })),
        { index: 'table_record_details', title: t('table.last_column_title'), size: 120 },
      ]
    : [];
  const tableData = (recordDataByTemplate[currentTemplate || 'voice'] || []).map((record) => ({
    ...record.data.reduce((acc, val) => {
      acc[val.alias_id] = val.value;
      return acc;
    }, {}),
    duration_records_items: (
      <div className="truncate w-full flex items-center justify-center">
        <TooltipWrapper
          content={tf('popup_hints.go_to_audio')}
          id={'preset'}
          className="flex cursor-pointer"
        >
          <TimeDuration
            link={`/${
              currentTemplate === 'chat' ? navigationRoutes.allChats : navigationRoutes.records
            }/${record[idByType[currentTemplate || 'voice']]}`}
            duration={record.duration}
          />
        </TooltipWrapper>
      </div>
    ),
    table_record_details: (
      <div className="flex items-center w-[110px] gap-[15px] justify-center">
        <DownloadCloudIcon
          hintTitle={t('popup_hints.download_record_button')}
          size={18}
          className={tableUtilIconsClassName}
          onClick={() =>
            downloadAudioHandler({ record_id: record[idByType[currentTemplate || 'voice']] })
          }
        />
        <Oscilogram
          hintTitle={t('popup_hints.to_record_title_button')}
          size={22}
          className={cn(tableUtilIconsClassName, 'text-bold')}
          onClick={() =>
            window.open(
              `/${
                currentTemplate === 'chat' ? navigationRoutes.allChats : navigationRoutes.records
              }/${record[idByType[currentTemplate || 'voice']]}`,
              '_blank',
            )
          }
        />
      </div>
    ),
  }));

  // const tableDatas =
  //   (recordDataByTemplate[currentTemplate || 'voice'] || []).map<TableItem[]>(
  //     (dataTable, record) => {
  //       const cell: TableItem = {};
  //       cell['duration_records_items'] = (

  //       );
  //       cell['table_record_details'] = ;
  //       for (const tableCell of record.data) {
  //         cell[tableCell.alias_id] = tableCell.value;
  //       }
  //       dataTable.push(cell);
  //       return dataTable;
  //     },
  //     [],
  //   ) || [];

  function paginatorChangePageHandler(paginationData: PaginationResponse) {
    getRecordsParams((prevState) => ({ ...prevState, ...paginationData }));
    updateUserSettings({
      ...userSettings,
      tablesLimit: {
        ...userSettings?.tablesLimit,
        analyticProject: {
          ...userSettings?.tablesLimit?.analyticProject,
          presets: paginationData.limit,
        },
      },
    });
  }
  function tableChangeFilterHandler(tableFilter: FilterTableData | undefined) {
    getRecordsParams((prevState) => ({ ...prevState, ...tableFilter }));
  }

  async function downloadAudioHandler(params: { record_id: string }) {
    return await getRecordAudio({ id: params.record_id }).then((res) =>
      fileDownload(
        res.data,
        res.headers['content-disposition'].replace(/"/gim, '').replace('attachment; filename=', ''),
      ),
    );
  }

  // function removeRecords() {
  //   changeDeleteRecordsIds((prevState) => {
  //     deleteRecordRequest({ items: prevState });
  //     return [];
  //   });
  // }
  const [tableLayouts, changeTableLayouts] = useState(
    userSettings?.tableColsSize?.analyticProject?.presets,
  );

  useEffect(() => {
    const condition = userSettings?.tableColsSize?.analyticProject?.presets?.find(
      (layoutItem) => layoutItem.id === id,
    );
    if (!condition && id) {
      const systemArr = [
        ...(userSettings?.tableColsSize?.analyticProject?.presets || []),
        { id: id, layout: [] },
      ];
      updateUserSettings({
        ...userSettings,
        tableColsSize: {
          ...userSettings?.tableColsSize,
          analyticProject: { ...userSettings?.tableColsSize?.analyticProject, presets: systemArr },
        },
      });
    } else {
      changeTableLayouts(userSettings?.tableColsSize?.analyticProject?.presets);
    }
  }, [id, updateUserSettings, userSettings]);

  function onChangeLayoutHandler(layout: ChartsLayoutType[]) {
    const currentChangedLayout = tableLayouts?.map((tablesLayout) => {
      if (tablesLayout.id === id) {
        return { id: tablesLayout.id, layout: layout.map((item) => ({ ...item, maxW: 24 })) };
      }
      return tablesLayout;
    });

    updateUserSettings({
      ...userSettings,
      tableColsSize: {
        ...userSettings?.tableColsSize,
        analyticProject: {
          ...userSettings?.tableColsSize?.analyticProject,
          presets: currentChangedLayout,
        },
      },
    });
  }
  const LoadingSkeleton = (
    <div className={'flex flex-col gap-[10px] pb-2'}>
      <Skeleton height={400} className={'mt-6'} />
      <Skeleton height={50} />
    </div>
  );
  if (isLoading) return LoadingSkeleton;

  return (
    <div className="relative">
      <div className="flex flex-col gap-[10px] pb-2">
        {userSettingsLoading && <Skeleton height={800} className={'absolute top-0 z-[999] mt-6'} />}
        <Table
          columns={tableTitles}
          dataSource={tableData}
          isPending={isLoading}
          layout={tableLayouts
            ?.find((layoutItem) => layoutItem.id === id)
            ?.layout.map((item) => ({ ...item, maxW: 16 }))}
          onLayoutChange={onChangeLayoutHandler}
          onFilter={tableChangeFilterHandler}
        />
        <Paginator
          limit={recordsParams.limit}
          page={Number(recordsParams.offset || 1)}
          count={Number(recordsList[currentTemplate || 'voice']?.total || 0)}
          onChange={paginatorChangePageHandler}
          pageOptions={pageOptionsData}
        />
      </div>
      {/*<Modal*/}
      {/*  id="deleteRecordFormModal"*/}
      {/*  value={Boolean(deleteRecordIds.length)}*/}
      {/*  setValue={changeDeleteRecordsIds.bind(null, [])}*/}
      {/*  title={*/}
      {/*    deleteRecordIds.length > 1 ? (*/}
      {/*      <>*/}
      {/*        {t('popover.delete_selected')}*/}
      {/*        <span className="text-basic_red">{changeDeleteRecordsIds.length}</span>*/}
      {/*      </>*/}
      {/*    ) : (*/}
      {/*      <>*/}
      {/*        {t('delete_record')} <span className="text-basic_red">{}</span>*/}
      {/*      </>*/}
      {/*    )*/}
      {/*  }*/}
      {/*  onApply={removeRecords}*/}
      {/*  onClose={changeDeleteRecordsIds.bind(null, [])}*/}
      {/*  loading={isDeleteLoading}*/}
      {/*  size="xs"*/}
      {/*  variant="removal"*/}
      {/*>*/}
      {/*  {deleteRecordIds.length > 1 ? t('delete_all_records') : t('record_wb_deleted')}*/}
      {/*</Modal>*/}
    </div>
  );
};
